import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Swal from 'sweetalert2'
import { PageTitle } from '../../../../../_metronic/layout/core'
import { FilterManager } from '../../../../common/filter/FilterManager'
import { fetchRiskScoreLogs } from '../../api/fetchLogs'
import { IFetchRiskScoreLogsParams, IRiskScoreLogItem } from '../../api/logs.types'
import { Table } from '../../../../../components/Table'
import { TableHead } from '../../../../../components/Table/TableHead'
import { TableBody } from '../../../../../components/Table/TableBody'
import { TablePagination } from '../../../../../components/Table/TablePagination'
import { convertToUTCByMSK, convertUTCtoMSK } from '../../../../../utils'
import {
  breadCrumbs,
  formatRiskScoreLogValue,
  ITEMS_PER_PAGE,
  RISK_SCORE_CHANGE_KIND_LABELS,
  SEARCH_FIELDS,
  SEARCH_FIELD_OPTIONS,
} from './core/consts'
import { getDefaultRiskScoreLogsFilters } from './core/query.defaults'
import { IRiskScoreLogsFilters } from './core/types'
import { ClientCell } from '../../../../common/ClientCell'

const DEFAULT_PAGE = 0

type RiskScoreLogsQuery = Omit<IFetchRiskScoreLogsParams, 'page' | 'perPage'>

const FALLBACK_ERROR = 'Не удалось загрузить логи риск-баллов'

const FIELD_LABELS: Record<string, string> = {
  externalUserId: 'ID клиента',
  externalUserToken: 'Токен клиента',
  dateStart: 'Дата с',
  dateEnd: 'Дата по',
}

/** Понятный текст вместо технического ответа бэкенда — по полю, а не по его сообщению. */
const FIELD_MESSAGES: Record<string, string> = {
  externalUserId: 'ID клиента указан неверно: пустое значение недопустимо.',
  externalUserToken:
    'Токен клиента указан неверно: ожидается UUID из 36 символов, например a2d9533f-fe1a-5c69-bd5d-7fbff8b0eb47.',
}

const getErrorMessage = (error: any): string => {
  // Глобальный перехватчик axios реджектит response, а не AxiosError — поле `response` есть
  // только когда ошибка пришла мимо него.
  const response = error?.data !== undefined ? error : error?.response
  const errors = response?.data?.errors

  if (response?.status === 404) {
    return 'Клиент с таким токеном не найден.'
  }

  if (!errors || typeof errors !== 'object') {
    return FALLBACK_ERROR
  }

  const [field] = Object.keys(errors)
  if (!field) {
    return FALLBACK_ERROR
  }

  if (FIELD_MESSAGES[field]) {
    return FIELD_MESSAGES[field]
  }

  if (field === 'general_error') {
    return String(errors[field])
  }

  return `${FIELD_LABELS[field] ?? field}: ${errors[field]}`
}

const buildQuery = (filters: IRiskScoreLogsFilters): RiskScoreLogsQuery => {
  const query: RiskScoreLogsQuery = {
    dateStart: filters.dateRange?.from ? convertToUTCByMSK(filters.dateRange.from) : null,
    dateEnd: filters.dateRange?.to ? convertToUTCByMSK(filters.dateRange.to) : null,
  }

  const search = filters.searchAdvanced?.query?.trim()
  if (search) {
    if (filters.searchAdvanced?.field === SEARCH_FIELDS.EXTERNAL_USER_TOKEN) {
      query.externalUserToken = search
    } else {
      query.externalUserId = search
    }
  }

  return query
}

export const RiskScoreLogs = () => {
  const defaultFilters = useMemo(() => getDefaultRiskScoreLogsFilters(), [])

  const [appliedQuery, setAppliedQuery] = useState<RiskScoreLogsQuery>(() =>
    buildQuery(defaultFilters)
  )
  const [items, setItems] = useState<IRiskScoreLogItem[]>([])
  const [totalRows, setTotalRows] = useState(0)
  const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE)
  const [isLoading, setIsLoading] = useState(false)

  const countPages = Math.ceil(totalRows / ITEMS_PER_PAGE)

  const fetchData = useCallback(async (page: number, query: RiskScoreLogsQuery) => {
    setIsLoading(true)
    try {
      const { data } = await fetchRiskScoreLogs({
        ...query,
        page: page + 1,
        perPage: ITEMS_PER_PAGE,
      })

      if (data.success) {
        setItems(data.riskScoreChanges ?? [])
        setTotalRows(data.totalRows ?? 0)
      }
    } catch (error) {
      setItems([])
      setTotalRows(0)
      Swal.fire('Ошибка', getErrorMessage(error), 'error')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchData(DEFAULT_PAGE, buildQuery(defaultFilters))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleApplyFilters = (filters: IRiskScoreLogsFilters) => {
    const query = buildQuery(filters)
    setAppliedQuery(query)
    setCurrentPage(DEFAULT_PAGE)
    fetchData(DEFAULT_PAGE, query)
  }

  const handlePageChange = (selected: number) => {
    setCurrentPage(selected)
    fetchData(selected, appliedQuery)
  }

  return (
    <div>
      <div className='card sticky-top-tickets mb-7'>
        <PageTitle breadcrumbs={breadCrumbs} />
        <div className='card-header border-0 pt-5 pb-2'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bold fs-3 mb-1'>Логи риск-баллов</span>
          </h3>
        </div>
      </div>

      <FilterManager<IRiskScoreLogsFilters>
        activeFilters={['dateRange', 'searchAdvanced']}
        defaultValues={defaultFilters}
        filterProps={{
          dateRange: { ranges: [] },
          searchAdvanced: {
            options: SEARCH_FIELD_OPTIONS,
            placeholder: 'Поиск клиента',
          },
        }}
        onApplyFilters={handleApplyFilters}
      />

      <div className='row g-5 g-xl-8 mb-7'>
        <div className='col'>
          <div className='card'>
            <div className='card-body'>
              <div className='card-rounded w-100'>
                <div className='d-flex flex-column'>
                  <Table>
                    <TableHead>
                      <tr>
                        <th>Дата</th>
                        <th>Клиент</th>
                        <th>Мерчант</th>
                        <th>Сотрудник</th>
                        <th>Что менялось</th>
                        <th>Старое значение</th>
                        <th>Новое значение</th>
                        <th>Причина</th>
                      </tr>
                    </TableHead>
                    <TableBody>
                      {isLoading ? (
                        <tr>
                          <td colSpan={8}>Идет загрузка...</td>
                        </tr>
                      ) : items.length ? (
                        items.map((item: IRiskScoreLogItem, index) => (
                          <tr key={index}>
                            <td>{convertUTCtoMSK(item.createdAt)}</td>
                            <ClientCell
                              externalId={item.externalUser?.externalId}
                              token={item.externalUser?.token}
                            />
                            <td>{item.externalUser?.merchantTitle || '-'}</td>
                            <td>{item.changedBy?.name || item.changedBy?.uuid || 'Система'}</td>
                            <td>{RISK_SCORE_CHANGE_KIND_LABELS[item.kind] || '-'}</td>
                            <td>{formatRiskScoreLogValue(item.kind, item.oldValue)}</td>
                            <td>{formatRiskScoreLogValue(item.kind, item.newValue)}</td>
                            <td>{item.reason}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={8}>Данных нет.</td>
                        </tr>
                      )}
                    </TableBody>
                  </Table>
                  <TablePagination
                    currentPage={currentPage}
                    pageCount={countPages}
                    onPageChange={handlePageChange}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
